import {
  DOCUMENT,
  InjectionToken,
  Signal,
  computed,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { APP_CONFIG, AppSource } from '@application/config';
import { LocalStorageFacade } from '@application/localstorage';
import { SelfUserStore } from '@domains/person/user/self/data';
import {
  IRefusalPushPresentation,
  IWebPushSubscribeRequest,
} from '../entities/web-push.model';
import {
  IWebPushSubscription,
  NOTIFICATION_WORKER,
  SYSTEM_NOTIFICATIONS,
} from '../infra/edge-control.tokens';
import { WebPushSubscriptionService } from '../infra/web-push-subscription.service';
import { MiniMonitorShortcutsStore } from './mini-monitor-shortcuts.store';

/**
 * De quem é a inscrição Web Push deste navegador: `<tenant>:<pessoa>`. É estado
 * da estação — o navegador tem uma inscrição só, e o servidor guarda-a por
 * pessoa e por conta.
 */
const OWNER_KEY = 'refusal-web-push-owner';

/** O máximo que "Sair" espera por desfazer a inscrição antes de sair na mesma. */
const SIGN_OUT_WAIT_MS = 1500;

/**
 * Até quando este navegador não volta a pedir a chave VAPID, depois de o
 * ambiente responder que não tem Web Push (404). O `SentryHttpErrorInterceptor`
 * reporta todo 404: sem isto, cada carga de quem tem a preferência ligada seria
 * um evento no Sentry enquanto o accessedgemanager não tiver Web Push.
 */
const UNAVAILABLE_UNTIL_KEY = 'refusal-web-push-unavailable-until';

/** Quanto tempo o 404 vale: depois disso, a carga seguinte pergunta de novo. */
const UNAVAILABLE_RETRY_MS = 6 * 60 * 60 * 1000;

/**
 * Se este navegador quer o Web Push da recusa. `null` enquanto a resposta não
 * chegou: sincronizar aí desinscreveria a cada carga.
 *
 * O padrão é a preferência "notificação do sistema" do mini-monitor, que é do
 * usuário e vem com as configurações dele. O app de controle de acesso remoto
 * troca a fonte pela sua, ligada pelo "Ativar avisos" do celular (T10.2).
 */
export const REFUSAL_PUSH_PREFERENCE = new InjectionToken<Signal<boolean | null>>(
  'REFUSAL_PUSH_PREFERENCE',
  {
    providedIn: 'root',
    factory: () => {
      const preferences = inject(MiniMonitorShortcutsStore);
      const selfUserStore = inject(SelfUserStore);
      return computed(() =>
        selfUserStore.userSettings() !== null ? preferences.refusalNotificationEnabled() : null
      );
    },
  }
);

interface IRefusalWebPushState {
  /** Os textos e endereços da notificação, entregues pela feature; `null` até ela montar. */
  presentation: IRefusalPushPresentation | null;
  /** A permissão de notificação deste navegador, relida quando muda. */
  permission: NotificationPermission | null;
  /** Este navegador está inscrito e o servidor confirmou-o nesta carga. */
  subscribed: boolean;
}

/**
 * O Web Push da recusa que ainda pode ser liberada: a notificação chega pelo
 * service worker mesmo com a aba suspensa, minimizada ou fechada — basta o
 * navegador aberto. A notificação da aba continua como está: as duas têm a
 * mesma tag (a tentativa), e a que chega depois substitui a outra sem avisar
 * de novo.
 *
 * Inscreve este navegador quando a preferência "notificação do sistema" do
 * usuário está ligada e a permissão do navegador está concedida; desinscreve
 * quando a preferência é desligada (aqui ou noutro computador — lido na carga
 * seguinte), quando a permissão é retirada, e no "Sair" (`signOut`), para que
 * num computador partilhado quem saiu deixe de receber recusas com o botão
 * "Liberar" a funcionar. Uma inscrição deixada por outra pessoa ou outra conta
 * (sessão que expirou sem "Sair") é desfeita na primeira carga de quem entra.
 *
 * Onde o ambiente não tem Web Push (`vapid-public-key` responde 404), não
 * inscreve e fica só a notificação da aba.
 */
export const RefusalWebPushStore = signalStore(
  { providedIn: 'root' },
  withState<IRefusalWebPushState>(() => ({
    presentation: null,
    permission: inject(SYSTEM_NOTIFICATIONS)?.permission ?? null,
    subscribed: false,
  })),
  withMethods(
    (
      store,
      worker = inject(NOTIFICATION_WORKER),
      notifications = inject(SYSTEM_NOTIFICATIONS),
      service = inject(WebPushSubscriptionService),
      storage = inject(LocalStorageFacade),
      appConfig = inject(APP_CONFIG),
      doc = inject(DOCUMENT)
    ) => {
      /** Depois do "Sair" nada volta a inscrever: a página está de saída. */
      let signedOut = false;

      /** Uma sincronização de cada vez, pela ordem em que foram pedidas. */
      let queue: Promise<void> = Promise.resolve();

      /**
       * A chave VAPID, pedida uma vez por carga. `null` quando o ambiente não
       * tem Web Push (404) — guardado também no navegador, por
       * {@link UNAVAILABLE_RETRY_MS}. Outro erro não fica guardado: a
       * sincronização seguinte tenta de novo.
       */
      let vapidPublicKey: Promise<string | null> | null = null;
      const publicKey = (): Promise<string | null> => {
        // quality-gate: ignore adhoc-format — epoch em ms que este store gravou, não é número mostrado ao usuário
        const unavailableUntil = Number(storage.getItem(UNAVAILABLE_UNTIL_KEY));
        if (unavailableUntil > DateTime.now().toMillis()) {
          return Promise.resolve(null);
        }
        vapidPublicKey ??= firstValueFrom(service.getVapidPublicKey()).then(
          ({ PublicKey }) => {
            storage.removeItem(UNAVAILABLE_UNTIL_KEY);
            return PublicKey || null;
          },
          (error: unknown) => {
            if ((error as HttpErrorResponse | null)?.status !== 404) {
              vapidPublicKey = null;
              throw error;
            }
            storage.setItem(
              UNAVAILABLE_UNTIL_KEY,
              String(DateTime.now().toMillis() + UNAVAILABLE_RETRY_MS)
            );
            return null;
          }
        );
        return vapidPublicKey;
      };

      /** base64url → bytes, a forma que o `pushManager.subscribe` aceita em todos os navegadores. */
      const keyBytes = (base64Url: string): Uint8Array => {
        const base64 = base64Url
          .replace(/-/g, '+')
          .replace(/_/g, '/')
          .padEnd(Math.ceil(base64Url.length / 4) * 4, '=');
        return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
      };

      /** A inscrição foi feita com esta chave? Com outra, o servidor não a consegue usar. */
      const sameKey = (
        subscription: IWebPushSubscription,
        expected: Uint8Array
      ): boolean => {
        const current = subscription.options.applicationServerKey;
        if (!current) return false;
        const bytes = new Uint8Array(current);
        return (
          bytes.length === expected.length &&
          bytes.every((byte, index) => byte === expected[index])
        );
      };

      const subscribeRequest = (
        subscription: IWebPushSubscription
      ): IWebPushSubscribeRequest => {
        const keys = subscription.toJSON().keys ?? {};
        if (!keys['p256dh'] || !keys['auth']) {
          throw new Error('Inscrição Web Push sem as chaves p256dh e auth.');
        }
        return {
          Endpoint: subscription.endpoint,
          Keys: { P256dh: keys['p256dh'], Auth: keys['auth'] },
          AppName: AppSource[appConfig.appSource] ?? null,
          UserAgent: doc.defaultView?.navigator.userAgent ?? null,
        };
      };

      /** Desfaz no servidor e no navegador; o servidor falhar não impede o navegador. */
      const unsubscribe = async (
        subscription: IWebPushSubscription
      ): Promise<void> => {
        const results = await Promise.allSettled([
          firstValueFrom(service.unsubscribeByEndpoint(subscription.endpoint)),
          subscription.unsubscribe(),
        ]);
        results
          .filter(
            (result): result is PromiseRejectedResult =>
              result.status === 'rejected'
          )
          .forEach(({ reason }) =>
            console.warn('Inscrição Web Push da recusa não desfeita:', reason)
          );
      };

      /**
       * Leva o navegador ao estado que a preferência, a permissão e a sessão
       * pedem. Idempotente: reenvia a inscrição a cada carga, porque o navegador
       * pode trocar o endpoint, e o servidor atualiza em vez de duplicar.
       */
      const sync = async (enabled: boolean, owner: string): Promise<void> => {
        if (signedOut || !worker) return;
        const wanted = enabled && notifications?.permission === 'granted';
        // Sem querer push, não se instala o worker em quem nunca o teve.
        const registration = wanted
          ? await worker.register()
          : await worker.existing();
        const push = registration?.pushManager;
        if (!push) {
          patchState(store, { subscribed: false });
          return;
        }

        let subscription = await push.getSubscription();
        if (subscription && storage.getItem(OWNER_KEY) !== owner) {
          // De quem esteve antes neste navegador: não é desta sessão pedir ao
          // servidor que a apague, mas o endpoint morto apaga-se lá no próximo envio.
          await subscription.unsubscribe();
          subscription = null;
        }

        if (!wanted) {
          if (subscription) await unsubscribe(subscription);
          storage.removeItem(OWNER_KEY);
          await worker.clearKit();
          patchState(store, { subscribed: false });
          return;
        }

        const key = await publicKey();
        if (!key || signedOut) {
          patchState(store, { subscribed: false });
          return;
        }
        const applicationServerKey = keyBytes(key);
        if (subscription && !sameKey(subscription, applicationServerKey)) {
          await subscription.unsubscribe();
          subscription = null;
        }
        // Antes de inscrever: outra aba a sincronizar ao mesmo tempo encontra a
        // inscrição já com dono, e não a desfaz como alheia.
        storage.setItem(OWNER_KEY, owner);
        subscription ??= await push.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
        // "Sair" durante a inscrição: a que acabou de nascer não pode sobreviver.
        if (signedOut) {
          await subscription.unsubscribe();
          return;
        }
        await firstValueFrom(service.subscribe(subscribeRequest(subscription)));
        if (signedOut) {
          await subscription.unsubscribe();
          return;
        }
        patchState(store, { subscribed: true });
      };

      return {
        /** Entregue pela feature, que tem o idioma, a rota do perfil e a marca. */
        setPresentation(presentation: IRefusalPushPresentation): void {
          patchState(store, { presentation });
        },

        /**
         * Relê a permissão deste navegador. O store já ouve a mudança pela
         * Permissions API; quem acabou de a pedir chama isto também, porque nem
         * todo navegador avisa.
         */
        refreshPermission(): void {
          patchState(store, { permission: notifications?.permission ?? null });
        },

        /** Pede uma sincronização, depois das que já estiverem na fila. */
        _requestSync(enabled: boolean, owner: string): void {
          queue = queue
            .then(() => sync(enabled, owner))
            .catch((error: unknown) => {
              patchState(store, { subscribed: false });
              console.warn('Web Push da recusa não sincronizado:', error);
            });
        },

        /** Guarda o kit do worker; só com inscrição, que é quando ele vai ser lido. */
        _saveKit(presentation: IRefusalPushPresentation): void {
          worker
            ?.saveKit({ ...presentation, releaseUrl: service.releaseUrl })
            .catch((error: unknown) =>
              console.warn('Textos do Web Push da recusa não guardados:', error)
            );
        },

        /**
         * Chamado pelo "Sair" antes de sair: desfaz a inscrição no servidor (ainda
         * com a sessão) e no navegador. Espera no máximo {@link SIGN_OUT_WAIT_MS}
         * — sair nunca fica preso a isto.
         */
        async signOut(): Promise<void> {
          signedOut = true;
          patchState(store, { subscribed: false });
          const work = (async () => {
            storage.removeItem(OWNER_KEY);
            const registration = await worker?.existing();
            const subscription = await registration?.pushManager?.getSubscription();
            await Promise.allSettled([
              subscription ? unsubscribe(subscription) : null,
              worker?.clearKit(),
            ]);
          })().catch((error: unknown) =>
            console.warn('Web Push da recusa não desfeito ao sair:', error)
          );
          let cap: ReturnType<typeof setTimeout> | undefined;
          await Promise.race([
            work,
            new Promise<void>((resolve) => {
              cap = setTimeout(resolve, SIGN_OUT_WAIT_MS);
            }),
          ]);
          clearTimeout(cap);
        },
      };
    }
  ),
  withHooks((store) => {
    const notifications = inject(SYSTEM_NOTIFICATIONS);
    const preference = inject(REFUSAL_PUSH_PREFERENCE);
    const selfUserStore = inject(SelfUserStore);
    let stopWatchingPermission: (() => void) | undefined;

    // A pessoa e a conta da sessão — as mesmas que o servidor lê do token.
    const owner = computed(() => {
      const profile = selfUserStore.userProfile();
      return profile?.TenantId && profile.PersonId
        ? `${profile.TenantId}:${profile.PersonId}`
        : null;
    });

    return {
      onInit(): void {
        stopWatchingPermission = notifications?.onPermissionChange(() =>
          store.refreshPermission()
        );

        effect(() => {
          const enabled = preference();
          const currentOwner = owner();
          store.permission();
          if (enabled === null || !currentOwner) return;
          untracked(() => store._requestSync(enabled, currentOwner));
        });

        effect(() => {
          const presentation = store.presentation();
          if (!presentation || !store.subscribed()) return;
          untracked(() => store._saveKit(presentation));
        });
      },
      onDestroy(): void {
        stopWatchingPermission?.();
      },
    };
  })
);
